import React, { useState, useEffect } from "react";
import { Button } from "carbon-components-react";
import { Renew16 } from "@carbon/icons-react";

import APIClient from "web_modules/APIClient";
import LabeledObject from "web_modules/shared/LabeledObject";
import HurdleView from "underwriter_dashboard/shared/HurdleView";
import { HurdleProps } from "underwriter_dashboard/HurdleEditor";
import {
  CashCloseTypes,
  CashCloseCashTradeInShape,
  HLOrderShape,
  humanizedCashCloseTypes,
  HumanizedHLStageTypes,
} from "@eave/shared/types/api";
import { formatCurrency } from "@eave/shared/utils/currency";
import { formatDateTimeString } from "@eave/shared/utils/date-formatter";
import { salesAppURL } from "underwriter_dashboard/shared";
import { connect } from "react-redux";

const styles = require("./styles.module.scss");

const EMDASH = <span>&mdash;</span>;

const cashCloseSummary = (hlOrder: HLOrderShape, cashCloseType: CashCloseTypes) => {
  return [
    {
      label: "Cash Close product",
      value: humanizedCashCloseTypes[cashCloseType],
    },
    {
      label: "Cash Close trade-in stage",
      value: hlOrder && hlOrder.cash_trade_in ? hlOrder.cash_trade_in.stage : EMDASH,
    },
    {
      label: "Cash Close cash offer stage",
      value: hlOrder && hlOrder.cash_offer ? hlOrder.cash_offer.stage : EMDASH,
    },
    {
      label: "Source",
      value: hlOrder && hlOrder.source ? hlOrder.source : EMDASH,
    },
    {
      label: "HL order ID",
      value: hlOrder && hlOrder.external_id ? hlOrder.external_id.toString() : EMDASH,
    },
  ];
};

const renderCashCloseSummary = (hlOrder: HLOrderShape, cashCloseType: CashCloseTypes) => {
  return (
    <LabeledObject title="Cash Close summary" listData={cashCloseSummary(hlOrder, cashCloseType)} />
  );
};

const renderMortgageLeadSummary = ({
  disbursementDate,
  closingDate,
  leadSource,
  leadNumber,
  loanOfficer,
  hlStage,
}) => {
  return (
    <>
      <LabeledObject
        title="Mortgage lead summary"
        listData={[
          {
            label: "Loan officer",
            value: loanOfficer.name || EMDASH,
          },
          {
            label: "Mortgage lead status",

            value: HumanizedHLStageTypes[hlStage] || EMDASH,
          },
          {
            label: "IR closing date",
            value: closingDate || EMDASH,
            tooltip: "Incoming residence closing date or signing date",
          },
          {
            label: "IR disbursement date",
            value: disbursementDate || EMDASH,
            tooltip: "Incoming residence disbursement date or close of escrow date",
          },
          {
            label: "Mortgage lead source",
            value: leadSource || EMDASH,
          },
          {
            label: "Mortgage lead ID",
            value:
              leadNumber > 0 ? (
                <a
                  rel="noreferrer"
                  target="_blank"
                  href={`${salesAppURL()}/mortgages/${leadNumber}`}
                >
                  {leadNumber}
                </a>
              ) : (
                EMDASH
              ),
          },
        ]}
      />
    </>
  );
};

const renderIncomingResidence = (hlOrder: HLOrderShape) => {
  const { cash_trade_in, cash_offer } = hlOrder;
  let incomingResidence: string;

  if (cash_trade_in) {
    incomingResidence = cash_trade_in.incoming_residence_address;
  }
  if (cash_offer) {
    incomingResidence = cash_offer.incoming_residence_address;
  }

  const listData = [
    {
      label: "Cash Close incoming residence address",
      value: incomingResidence || EMDASH,
    },
    {
      label: "Cash Close fee",
      tooltip: "Cash Close fee is only applicable for cash offer deals",
      value:
        cash_offer && cash_offer.incoming_residence_cash_close_fee
          ? formatCurrency(cash_offer.incoming_residence_cash_close_fee)
          : EMDASH,
    },
    {
      label: "Cash Offer IR Purchase COE",
      value:
        cash_offer && cash_offer.incoming_residence_coe
          ? cash_offer.incoming_residence_coe
          : EMDASH,
    },
  ];

  return (
    <>
      <h3>Incoming Residence</h3>
      <LabeledObject listData={listData} />
    </>
  );
};

const renderDepartingResidence = ({
  cashTradeIn,
  departingResidenceLoanPayOffEstimate,
}: {
  cashTradeIn: CashCloseCashTradeInShape;
  departingResidenceLoanPayOffEstimate: number;
}) => {
  const {
    departing_residence_address,
    departing_residence_escrow_ordered,
    departing_residence_preliminary_home_value,
    departing_residence_home_valuation,
    departing_residence_guaranteed_sale_price,
    departing_residence_disbursement_date,
  } = cashTradeIn;
  const listData = [
    {
      label: "Cash Close departing residence",
      value: departing_residence_address || EMDASH,
    },
    {
      label: "DR disbursement date",
      value: departing_residence_disbursement_date || EMDASH,
    },
    {
      label: "Escrow ordered",
      value: departing_residence_escrow_ordered || EMDASH,
    },
    {
      label: "HL preliminary home value (TI)",
      value: departing_residence_preliminary_home_value
        ? formatCurrency(departing_residence_preliminary_home_value)
        : EMDASH,
    },
    {
      label: "HL home value (TI)",
      value: departing_residence_home_valuation
        ? formatCurrency(departing_residence_home_valuation)
        : EMDASH,
    },
    {
      label: "HL guaranteed sale price (TI)",
      value: departing_residence_home_valuation
        ? formatCurrency(departing_residence_guaranteed_sale_price)
        : EMDASH,
    },
    {
      label: "Mortgage balances / Loan payoff sent to CC",
      value: departingResidenceLoanPayOffEstimate
        ? formatCurrency(departingResidenceLoanPayOffEstimate)
        : EMDASH,
    },
  ];

  return (
    <>
      <h3>Departing Residence (DR) - Trade-In (TI)</h3>
      <LabeledObject listData={listData} />
    </>
  );
};

interface CashCloseSummaryData {
  hlOrder: HLOrderShape;
  disbursementDate: any;
  closingDate: any;
  leadSource: string;
  leadNumber: string;
  loanOfficer: any;
  hlStage: string;
  departingResidenceLoanPayOffEstimate: number;
  cashCloseType: CashCloseTypes;
}

interface Props extends HurdleProps {}

const CashCloseSummaryHurdle = (props: Props) => {
  const { app, hurdle, onHide } = props;

  const [cashCloseData, setCashCloseData] = useState<CashCloseSummaryData | null>(null);
  useEffect(() => {
    APIClient.get<{
      cash_close_summary: {
        hl_order: HLOrderShape;
        disbursement_date: any;
        closing_date: any;
        lead_source: any;
        lead_number: any;
        loan_officer: any;
        hl_stage: string;
        departing_residence_loan_pay_off_estimate: number;
        cash_close_type: CashCloseTypes;
      };
    }>(`/uw/loan_applications/${app.id}/cash_close`).then(resp => {
      const {
        cash_close_summary: {
          hl_order,
          disbursement_date,
          closing_date,
          lead_source,
          lead_number,
          loan_officer,
          hl_stage,
          departing_residence_loan_pay_off_estimate,
          cash_close_type,
        },
      } = resp;

      setCashCloseData({
        closingDate: closing_date,
        departingResidenceLoanPayOffEstimate: departing_residence_loan_pay_off_estimate,
        disbursementDate: disbursement_date,
        hlOrder: hl_order,
        leadNumber: lead_number,
        leadSource: lead_source,
        loanOfficer: loan_officer,
        hlStage: hl_stage,
        cashCloseType: cash_close_type,
      });
    });
  }, [app]);

  const makeCashCloseRefreshRequest = appId =>
    APIClient.put<{ order: HLOrderShape; cash_close_type: CashCloseTypes }>(
      `/uw/loan_applications/${appId}/cash_close/refresh`,
      {},
    ).then(resp => {
      const { order, cash_close_type } = resp;

      setCashCloseData({
        ...cashCloseData,
        hlOrder: order,
        cashCloseType: cash_close_type,
      });
    });

  if (!cashCloseData) {
    return <h3>Loading ...</h3>;
  }

  const {
    closingDate,
    departingResidenceLoanPayOffEstimate,
    disbursementDate,
    hlOrder,
    leadNumber,
    leadSource,
    loanOfficer,
    hlStage,
    cashCloseType,
  } = cashCloseData;

  return (
    <HurdleView app={app} hurdle={hurdle} onHide={onHide}>
      <div className={styles.ctaWrapper}>
        <Button
          onClick={() => {
            makeCashCloseRefreshRequest(app.id);
          }}
          renderIcon={Renew16}
        >
          Refresh Cash Close Data
        </Button>
        <span className={styles.updated}>
          Last updated:{" "}
          {hlOrder && hlOrder.updated_at ? formatDateTimeString(hlOrder.updated_at) : EMDASH}
        </span>
      </div>

      <div className={styles.cashCloseContainer}>
        <div className={styles.listWrapper}>{renderCashCloseSummary(hlOrder, cashCloseType)}</div>
        <div className={styles.listWrapper}>
          {renderMortgageLeadSummary({
            closingDate,
            disbursementDate,
            leadNumber,
            leadSource,
            loanOfficer,
            hlStage,
          })}
        </div>
        {hlOrder && hlOrder.cash_trade_in && (
          <div className={styles.listWrapper}>
            {renderDepartingResidence({
              cashTradeIn: hlOrder.cash_trade_in,
              departingResidenceLoanPayOffEstimate,
            })}
          </div>
        )}
        {hlOrder && (hlOrder.cash_trade_in || hlOrder.cash_offer) && (
          <div className={styles.listWrapper}>{renderIncomingResidence(hlOrder)}</div>
        )}
      </div>
    </HurdleView>
  );
};

export default connect(null, null)(CashCloseSummaryHurdle);
